import React from "react";
import Grid from '@mui/material/Grid2';
import { TicketAttachment } from "@services/tickets/types";
import { displayDateTime } from "@common/utils/localTime.utils";
import { IconButton, Tooltip, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import ConsoleLog from "@pages/Tickets/ConsoleLog";
import Typography from "@common/components/Typography";
import { Add, Delete } from "@mui/icons-material";
import { useContext, useState } from "react";
import { Link } from "@mui/material";
import DeleteAttachmentDialog from "../../DeleteAttachmentDialog";
import { FormContext } from "../../context/formContext";
import { getKBSize } from "@common/utils/translation";
import UploadDialog, { UploadType } from "@common/components/Logo/UploadDialog";
import { getTicketAttachment } from "@services/tickets";

const Attachments = () => {
  const { t } = useTranslation();
  const { form, canEdit, reset, dispatchHasChanged } = useContext(FormContext);
  const { ticket, details } = form;
  const [openAddAttachmentDialog, setOpenAddAttachmentDialog] =
    useState<boolean>(false);
  const [attachmentId, setAttachmentId] = useState<number>();
  const [openDeleteAttachmentDialog, setOpenDeleteAttachmentDialog] =
    useState<boolean>(false);
  const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.text.primary,
    cursor: "pointer",
    fontFamily: "Realtime",
    fontSize: "0.9rem",
  }));
  const disabled = ticket?.status == "Closed" || !canEdit;

  const handleDownload = async (id: number, fileName: string, url?: string) => {
    var tempLink = document.createElement("a");
    tempLink.style.display = "none";
    if (url) {
      tempLink.href = url;
      tempLink.setAttribute("target", "_blank");
    } else {
      let blob = await getTicketAttachment(id);

      var blobURL =
        window.URL && window.URL.createObjectURL
          ? window.URL.createObjectURL(blob)
          : window.webkitURL.createObjectURL(blob);

      tempLink.href = blobURL;
      tempLink.setAttribute("download", fileName);

      // Safari thinks _blank anchor are pop ups. We only want to set _blank
      // target if the browser does not support the HTML5 download attribute.
      // This allows you to download files in desktop safari if pop up blocking
      // is enabled.
      if (typeof tempLink.download === "undefined") {
        tempLink.setAttribute("target", "_blank");
      }
    }
    document.body.appendChild(tempLink);
    tempLink.click();

    // Fixes "webkit blob resource error 1"
    setTimeout(function () {
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(blobURL);
    }, 200);
  };

  const deleteAttachmentAction = (id: number) => {
    setAttachmentId(id);
    setOpenDeleteAttachmentDialog(true);
  };

  const displayAttachmentsContent = () => {
    return details && details.attachments ? (
      details?.attachments
        .sort((a, b) => (a?.createdDate ?? 0) - (b?.createdDate ?? 0))
        .map((attachment: TicketAttachment, index: number) => {
          return (
            <Grid key={`lineAttachments-${index}`}>
              <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid>
                  <ConsoleLog>
                    <Typography realTime>
                      {displayDateTime(attachment.createdDate)}
                    </Typography>
                  </ConsoleLog>
                </Grid>
                <Grid>
                  <Typography realTime>
                    {t("Added By {{creator}}", { creator: attachment.creator })}
                  </Typography>
                </Grid>
                <Grid
                  sx={{
                    flex: "1",
                  }}>
                  <StyledLink
                    onClick={() => {
                      handleDownload(
                        attachment.id,
                        attachment.name ?? "error",
                        attachment.url
                      );
                    }}
                  >
                    {`${attachment.name} (${getKBSize(
                      attachment.size ?? 0,
                      t
                    )})`}
                  </StyledLink>
                </Grid>
                <Grid>
                  <IconButton
                    disabled={disabled}
                    onClick={() => deleteAttachmentAction(attachment.id)}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          );
        })
    ) : (
      <></>
    );
  };

  return (
    <>
      <Grid
        container
        sx={(theme) => ({
          padding: theme.spacing(2, 4),
          flexDirection: "column",
        })}
        spacing={2}
      >
        <Grid>
          <Grid
            container
            spacing={2}
            sx={{ width: '100%', flexDirection: "row", }}
          >
            <Grid>{t("Attachments")}</Grid>
            <Tooltip title={t("Add") as string} arrow>
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => setOpenAddAttachmentDialog(true)}
                  disabled={disabled}
                  sx={{
                    marginTop: 1.5,
                    marginLeft: 0.75,
                  }}
                >
                  <Add />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid>
          <Grid
            container
            spacing={1}
            sx={{ width: '100%', flexDirection: "column", }}
          >
            {displayAttachmentsContent()}
          </Grid>
        </Grid>
      </Grid>
      <UploadDialog
        objectId={ticket.id}
        open={openAddAttachmentDialog}
        onClose={() => setOpenAddAttachmentDialog(false)}
        refetch={reset}
        uploadType={UploadType.Ticket}
        title={t("Add Attachment")}
        onSuccess={() => dispatchHasChanged(true)}
      />
      <DeleteAttachmentDialog
        open={openDeleteAttachmentDialog}
        onClose={() => setOpenDeleteAttachmentDialog(false)}
        attachmentId={attachmentId}
      ></DeleteAttachmentDialog>
    </>
  );
};

export default Attachments;
